// "My bubble color" (v1.2.2 mobile parity). The user's OWN chat bubbles re-tint to a chosen preset.
// Keys mirror the backend exactly (mint/classic/cyan/blue/purple/graphite) and persist via
// PATCH /profile → returned on /auth/me. Sync contract (mirrors mobile): server value wins on
// login/app-open (overwrites local); on user change we apply locally first (instant) then
// fire-and-forget the PATCH. Only the owner sees their own bubbles tinted — recipients see standard
// incoming bubbles (the backend intentionally omits bubble_color from message sender objects).
import { useEffect, useState } from "react";

const STORE = "bloq_bubble_color";
const EVENT = "bloq-bubble-color";

// 6 presets keyed to the backend. `grad` = tailwind gradient for the own-bubble fill; `text` keeps
// contrast readable on light vs dark tints; `swatch` renders the Profile picker chip.
export const BUBBLE_COLORS = [
  { key: "mint", label: "Mint", grad: "from-bloq-mint/90 to-bloq-blue/90", text: "text-bloq-bg", swatch: "linear-gradient(135deg,#01FABD,#3591F4)" },
  { key: "classic", label: "Classic", grad: "from-sky-500 to-blue-600", text: "text-white", swatch: "linear-gradient(135deg,#0ea5e9,#2563eb)" },
  { key: "cyan", label: "Cyan", grad: "from-cyan-400 to-teal-500", text: "text-bloq-bg", swatch: "linear-gradient(135deg,#22d3ee,#14b8a6)" },
  { key: "blue", label: "Blue", grad: "from-blue-500 to-indigo-600", text: "text-white", swatch: "linear-gradient(135deg,#3b82f6,#4f46e5)" },
  { key: "purple", label: "Purple", grad: "from-violet-500 to-purple-600", text: "text-white", swatch: "linear-gradient(135deg,#8b5cf6,#9333ea)" },
  { key: "graphite", label: "Graphite", grad: "from-slate-500 to-slate-700", text: "text-white", swatch: "linear-gradient(135deg,#64748b,#334155)" },
];

export const DEFAULT_BUBBLE_KEY = "mint";
const VALID = new Set(BUBBLE_COLORS.map((c) => c.key));

function normalize(key) {
  return VALID.has(key) ? key : DEFAULT_BUBBLE_KEY;
}

export function getBubbleKey() {
  try {
    return normalize(localStorage.getItem(STORE) || DEFAULT_BUBBLE_KEY);
  } catch {
    return DEFAULT_BUBBLE_KEY;
  }
}

// Apply locally + broadcast (does NOT hit the network — callers persist separately).
export function setBubbleKey(key) {
  const k = normalize(key);
  try {
    localStorage.setItem(STORE, k);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: k }));
}

export function bubbleClasses(key) {
  const c = BUBBLE_COLORS.find((x) => x.key === normalize(key)) || BUBBLE_COLORS[0];
  return `${c.grad} ${c.text}`;
}

// Live-updating hook — re-renders whenever the preset changes anywhere in the app.
export function useBubbleColor() {
  const [key, setKey] = useState(getBubbleKey);
  useEffect(() => {
    const onChange = (e) => setKey(e.detail || getBubbleKey());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);
  return key;
}
